import { IFieldInfoProperties, isConditionallyRequired } from "@components/smart/FieldInfo";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import BindingContext, { IEntity } from "@odata/BindingContext";
import { isDefined, isObjectEmpty } from "@utils/general";
import memoize from "@utils/memoize";
import { anyPartStartsWithAccentsInsensitive } from "@utils/string";

import { ValueType } from "../../enums";
import { FormStorage, IFormStorageDefaultCustomData, IGroupStatus } from "./FormStorage";

interface IVirtualizedFormStorageCustomData extends IFormStorageDefaultCustomData {
    searchTerm: string; // custom data field to store search term for list group summary item
}

export type TSmartVirtualizedFormStorage = FormStorage<IEntity, IVirtualizedFormStorageCustomData>;
export const CUSTOMIZE_FIELDS_ACTION = "customize_fields";

export const getHidableFields = memoize((storage: TSmartVirtualizedFormStorage, baseBc: BindingContext, def: IFormGroupDef): string[] => {
    const flattenedRows = (def.rows || []).flat();
    return flattenedRows
        .filter(fieldDef => {
            const bc = baseBc.navigate(fieldDef.id);
            const info = storage.getInfo(bc);
            const hasVisibility = isDefined(info.isVisible);
            const _isReq = isConditionallyRequired({ storage, bindingContext: bc, info });
            return !hasVisibility && !_isReq;
        })
        .map(fieldDef => fieldDef.id);
}, (storage, baseBc, def) =>
    `${baseBc.getFullPath()}::${def.id}`);

export function isFieldMatchingSearchTerm(field: IFieldInfoProperties, searchTerm: string): boolean {
    const { label, metadata } = field;
    return anyPartStartsWithAccentsInsensitive(label, searchTerm, ValueType.String)
        || (metadata?.id && anyPartStartsWithAccentsInsensitive(metadata.id.toString(), searchTerm, ValueType.Number));
}

/**
 * ============= Methods to handle field visibility in Group Status =============
 */
export function isFieldHidden(storage: TSmartVirtualizedFormStorage, groupBc: BindingContext, groupDef: IFormGroupDef, fieldId: string, fieldBc: BindingContext): boolean {
    const groupPath = groupBc.getNavigationPath(false);
    const { visibleFields } = getCurrentVisibilityStatus(storage, groupDef.id, groupPath);
    const hasValue = !isObjectEmpty(storage.getValue(fieldBc, { useDirectValue: false }));
    if (hasValue) {
        return false;
    }
    return getHidableFields(storage, groupBc, groupDef).includes(fieldId)
        && visibleFields[groupPath][fieldId] !== true;
}

function getCurrentVisibilityStatus(storage: TSmartVirtualizedFormStorage, groupId: string, groupPath: string): IGroupStatus {
    const status = storage.getGroupStatus(groupId) ?? {};
    if (!status.visibleFields) {
        status.visibleFields = {};
    }
    if (!status.visibleFields[groupPath]) {
        status.visibleFields[groupPath] = {};
    }
    return status;
}

export function getVisibleFields(storage: TSmartVirtualizedFormStorage, groupBc: BindingContext, groupDef: IFormGroupDef): string[] {
    return getHidableFields(storage, groupBc, groupDef).filter(fieldId =>
        !isFieldHidden(storage, groupBc, groupDef, fieldId, groupBc.navigate(fieldId)));
}

export function setFieldVisibility(storage: TSmartVirtualizedFormStorage, fieldBc: BindingContext, isVisible = false): void {
    const groupId = storage._getGroupKeyFromBc(fieldBc);
    let groupBc = fieldBc.getParent();
    while (!groupBc.isCollection() && !groupBc.isRoot()) {
        groupBc = groupBc.getParent();
    }

    const fieldId = fieldBc.getNavigationPath(true, groupBc);
    const groupPath = groupBc.getNavigationPath(false);
    const status = getCurrentVisibilityStatus(storage, groupId, groupPath);
    status.visibleFields[groupPath][fieldId] = isVisible;
    storage.setGroupStatus(status, groupId);
}

export function setMassFieldsVisibility(storage: TSmartVirtualizedFormStorage, groupBc: BindingContext, groupDef: IFormGroupDef, selectedRows: string[]): void {
    const _isSelected = (rowId: string, bc: BindingContext): boolean => {
        if (!isDefined(selectedRows)) {
            const val = storage.getValue(bc);
            return !(isObjectEmpty(val) || !val);
        }

        return selectedRows.includes(rowId);
    };

    const groupPath = groupBc.getNavigationPath(false);
    const status = getCurrentVisibilityStatus(storage, groupDef.id, groupPath);
    getHidableFields(storage, groupBc, groupDef).forEach(rowId => {
        const bc = groupBc.navigate(rowId);
        const isSelected = _isSelected(rowId, bc);
        if (!isSelected) {
            storage.clearValue(bc);
            storage.addActiveFieldsFromAffectedFields(bc);
        }
        status.visibleFields[groupPath][rowId] = isSelected;
    });
    storage.setGroupStatus(status, groupDef.id);
}

/**
 * =====END===== Methods to handle field visibility in Group Status =====END=====
 */